import { InputField, SelectField } from "../../UI/FormFields";

interface Step2Data {
  maiorTitulacao: string;
  nomeCursoTitulacao: string;
  instituicaoTitulacao: string;
  mesTitulacao: string;
  anoTitulacao: string;
  nomeCursoAndamento: string;
  periodoMatricula: string;
  grandeAreaConhecimento: string;
  subAreaConhecimento: string;
  anoMatriculaAtual: string;
  municipioCurso: string;
  ufCurso: string;
}

interface Step2Props {
  data: Step2Data;
  errors: Partial<Record<keyof Step2Data, string>>;
  onChange: (name: keyof Step2Data, value: string) => void;
}

export default function Step2InformacoesAcademicas({
  data,
  errors,
  onChange,
}: Step2Props) {
  const titulacaoOptions = [
    { value: "Pós-Doutorado", label: "Pós-Doutorado" },
    { value: "Doutorado", label: "Doutorado" },
    { value: "Mestrado", label: "Mestrado" },
    { value: "Especialização", label: "Especialização" },
    { value: "Graduação", label: "Graduação" },
  ];

  const mesesOptions = [
    { value: "01", label: "Janeiro" },
    { value: "02", label: "Fevereiro" },
    { value: "03", label: "Março" },
    { value: "04", label: "Abril" },
    { value: "05", label: "Maio" },
    { value: "06", label: "Junho" },
    { value: "07", label: "Julho" },
    { value: "08", label: "Agosto" },
    { value: "09", label: "Setembro" },
    { value: "10", label: "Outubro" },
    { value: "11", label: "Novembro" },
    { value: "12", label: "Dezembro" },
  ];

  const grandesAreasOptions = [
    { value: "ciencias_exatas", label: "Ciências Exatas e da Terra" },
    { value: "ciencias_biologicas", label: "Ciências Biológicas" },
    { value: "engenharias", label: "Engenharias" },
    { value: "ciencias_saude", label: "Ciências da Saúde" },
    { value: "ciencias_agrarias", label: "Ciências Agrárias" },
    { value: "ciencias_sociais", label: "Ciências Sociais Aplicadas" },
    { value: "ciencias_humanas", label: "Ciências Humanas" },
    { value: "linguistica", label: "Linguística, Letras e Artes" },
  ];

  const ufsOptions = [
    { value: "AC", label: "Acre" },
    { value: "AL", label: "Alagoas" },
    { value: "AP", label: "Amapá" },
    { value: "AM", label: "Amazonas" },
    { value: "BA", label: "Bahia" },
    { value: "CE", label: "Ceará" },
    { value: "DF", label: "Distrito Federal" },
    { value: "ES", label: "Espírito Santo" },
    { value: "GO", label: "Goiás" },
    { value: "MA", label: "Maranhão" },
    { value: "MT", label: "Mato Grosso" },
    { value: "MS", label: "Mato Grosso do Sul" },
    { value: "MG", label: "Minas Gerais" },
    { value: "PA", label: "Pará" },
    { value: "PB", label: "Paraíba" },
    { value: "PR", label: "Paraná" },
    { value: "PE", label: "Pernambuco" },
    { value: "PI", label: "Piauí" },
    { value: "RJ", label: "Rio de Janeiro" },
    { value: "RN", label: "Rio Grande do Norte" },
    { value: "RS", label: "Rio Grande do Sul" },
    { value: "RO", label: "Rondônia" },
    { value: "RR", label: "Roraima" },
    { value: "SC", label: "Santa Catarina" },
    { value: "SP", label: "São Paulo" },
    { value: "SE", label: "Sergipe" },
    { value: "TO", label: "Tocantins" },
  ];

  const currentYear = new Date().getFullYear();
  const anosOptions = Array.from({ length: 50 }, (_, i) => {
    const year = currentYear - i;
    return { value: year.toString(), label: year.toString() };
  });

  return (
    <div className="space-y-8 animate-fadeIn">
      <div className="bg-gray-50 p-6 mb-8 rounded-lg shadow-sm">
        <h3 className="text-xl font-bold text-gray-900 mb-2 uppercase tracking-wide">
          Informações Acadêmicas e Profissionais
        </h3>
        <p className="text-sm text-gray-600">
          Informe sua formação acadêmica e profissional. Campos marcados com{" "}
          <span className="text-red-600 font-semibold">*</span> são
          obrigatórios.
        </p>
      </div>

      {/* Maior Titulação */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5">
        <SelectField
          label="Maior Titulação"
          name="maiorTitulacao"
          value={data.maiorTitulacao}
          onChange={(e) => onChange("maiorTitulacao", e.target.value)}
          options={titulacaoOptions}
          required
          error={errors.maiorTitulacao}
        />
      </div>

      {/* Informações da Titulação */}
      <div className="border-t pt-6">
        <h4 className="text-md font-semibold text-gray-800 mb-4">
          Dados da Titulação
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <InputField
              label="Nome do Curso da Maior Titulação"
              name="nomeCursoTitulacao"
              value={data.nomeCursoTitulacao}
              onChange={(e) => onChange("nomeCursoTitulacao", e.target.value)}
              required
              error={errors.nomeCursoTitulacao}
              placeholder="Ex: Ciência da Computação, Engenharia Civil, etc."
            />
          </div>

          <div className="md:col-span-2">
            <InputField
              label="Instituição que Concedeu a Titulação"
              name="instituicaoTitulacao"
              value={data.instituicaoTitulacao}
              onChange={(e) => onChange("instituicaoTitulacao", e.target.value)}
              required
              error={errors.instituicaoTitulacao}
              placeholder="Nome da universidade ou instituição"
            />
          </div>

          <SelectField
            label="Mês de Titulação"
            name="mesTitulacao"
            value={data.mesTitulacao}
            onChange={(e) => onChange("mesTitulacao", e.target.value)}
            options={mesesOptions}
            required
            error={errors.mesTitulacao}
          />

          <SelectField
            label="Ano de Titulação"
            name="anoTitulacao"
            value={data.anoTitulacao}
            onChange={(e) => onChange("anoTitulacao", e.target.value)}
            options={anosOptions}
            required
            error={errors.anoTitulacao}
          />
        </div>
      </div>

      {/* Curso em Andamento (opcional) */}
      <div className="border-t pt-6">
        <h4 className="text-md font-semibold text-gray-800 mb-4">
          Curso em Andamento (opcional)
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <InputField
              label="Nome do Curso em Andamento"
              name="nomeCursoAndamento"
              value={data.nomeCursoAndamento}
              onChange={(e) => onChange("nomeCursoAndamento", e.target.value)}
              placeholder="Deixe em branco se não estiver cursando"
            />
          </div>

          <InputField
            label="Período da Matrícula Atual"
            name="periodoMatricula"
            value={data.periodoMatricula}
            onChange={(e) => onChange("periodoMatricula", e.target.value)}
            placeholder="Ex: 3º semestre, 2º ano, etc."
          />

          <InputField
            label="Ano da Matrícula Atual"
            name="anoMatriculaAtual"
            value={data.anoMatriculaAtual}
            onChange={(e) => onChange("anoMatriculaAtual", e.target.value)}
            placeholder="Ex: 2024"
            maxLength={4}
          />
        </div>
      </div>

      {/* Área de Conhecimento */}
      <div className="border-t pt-6">
        <h4 className="text-md font-semibold text-gray-800 mb-4">
          Área de Conhecimento
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <SelectField
            label="Grande Área de Conhecimento"
            name="grandeAreaConhecimento"
            value={data.grandeAreaConhecimento}
            onChange={(e) => onChange("grandeAreaConhecimento", e.target.value)}
            options={grandesAreasOptions}
          />

          <InputField
            label="Sub-área de Conhecimento"
            name="subAreaConhecimento"
            value={data.subAreaConhecimento}
            onChange={(e) => onChange("subAreaConhecimento", e.target.value)}
            placeholder="Ex: Inteligência Artificial, Medicina, etc."
          />
        </div>
      </div>

      {/* Localização do Curso */}
      <div className="border-t pt-6">
        <h4 className="text-md font-semibold text-gray-800 mb-4">
          Localização do Curso
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <InputField
            label="Município (curso)"
            name="municipioCurso"
            value={data.municipioCurso}
            onChange={(e) => onChange("municipioCurso", e.target.value)}
            placeholder="Nome do município"
          />

          <SelectField
            label="UF (curso)"
            name="ufCurso"
            value={data.ufCurso}
            onChange={(e) => onChange("ufCurso", e.target.value)}
            options={ufsOptions}
          />
        </div>
      </div>
    </div>
  );
}
